import { CloseOutlined, DeleteOutlined, EditOutlined, LeftOutlined, MoreOutlined } from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MenuProps, message } from 'antd';
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { deleteTask, moveTaskToPlaning } from '../../api/task.api';
import { EstimateIcon } from '../../assets/CustomIcons/EstimateIcon';
import { GoToIcon } from '../../assets/CustomIcons/GoToIcon';
import { SubitemIcon } from '../../assets/CustomIcons/SubitemIcon';
import { useNavigateToTask } from '../../hooks/useTaskNavigation';
import { ViewContext } from '../../pages/Space/Context/ViewContext';
import { queryClient } from '../../queryClient';
import { optimisticUpdateListItem } from '../../serverState/helpers/optimisticCache';
import { queryKeys } from '../../serverState/queryKeys';
import { useCurrentWorkspaceEpics } from '../../serverState/epics';
import { removeFromSpaceTasksOrder } from '../../serverState/helpers/tasksOrderHelper';
import { getCurrentWorkspaceActiveSprint } from '../../serverState/sprint';
import { useUpdateTaskMutation } from '../../serverState/tasks';
import { useGetUsersWithWorkspaceAccess, useGetWorkspace } from '../../serverState/workspace';
import { Epic } from '../../types/Epic';
import Task from '../../types/Task';
import { defaultTaskStatuses, TaskStatus } from '../../types/TaskStatus';
import { TaskType } from '../../types/TaskType';
import { WorkspaceEstimationType, WorkspaceFramework } from '../../types/Workspace';
import { WorkspaceViewGroup } from '../../types/WorkspaceView';
import { DeleteTaskModal } from '../Modal/DeleteTaskModal';
import { PriorityOptions, UserAvatar } from '../TaskTable/helpers/helper';
import { BaseButton } from '../UI/BaseButton';
import { OwnerDropdown } from '../UI/Dropdown';
import BaseDropdown from '../UI/Dropdown/BaseDropdown';
import EpicDropdown from '../UI/Dropdown/EpicDropdown';
import EstimateDropdown from '../UI/Dropdown/EstimateDropdown';
import ParentTaskDropdown from '../UI/Dropdown/ParentTaskDropdown';
import PriorityDropdown from '../UI/Dropdown/PriorityDropdown';
import TaskTypeDropdown from '../UI/Dropdown/TaskTypeDropdown';
import CustomTooltip from '../UI/Tooltip/CustomTooltip';
import { WorkSpaceContext } from './Board';
import './NewTaskCard/NewTaskCard.scss';
import { TitleInput, TitleInputRef } from './NewTaskCard/TitleInput';

interface Props {
  task: Task;
  dragging?: boolean;
}

function TaskCard({ task, dragging }: Props) {
  const context = useContext(WorkSpaceContext);
  if (!context) {
    return null;
  }

  const viewContext = useContext(ViewContext);
  const workspaceQuery = useGetWorkspace();
  const taskStatuses: Record<TaskStatus['id'], TaskStatus> = (
    (workspaceQuery.data?.TaskStatus as TaskStatus[]) || defaultTaskStatuses
  ).reduce((prev, curr) => ({ ...prev, [curr.id]: curr }), {});
  const usersWithAccessToSpace = useGetUsersWithWorkspaceAccess(workspaceQuery.data?.id as number).data || [];
  const epicsQuery = useCurrentWorkspaceEpics();
  const epics = epicsQuery.data || [];

  const estimationType = context.workspace?.estimationType;

  // Derive display values from task prop — no local state duplication
  const assignee = useMemo(
    () =>
      task.assignee
        ? (usersWithAccessToSpace.find((userAccess) => userAccess.User.id === task.assignee)?.User ?? null)
        : null,
    [task.assignee, usersWithAccessToSpace]
  );
  const estimate = useMemo(
    () => (estimationType === WorkspaceEstimationType.storyPoints ? (task.storyPoints ?? task.estimate ?? null) : null),
    [task.storyPoints, task.estimate, estimationType]
  );
  const priority = task.priority ?? null;
  const taskType = task.type !== undefined ? (task.type as TaskType) : null;
  const epic = useMemo(
    () => (task.epicId ? (epics.find((e: Epic) => e.id === task.epicId) ?? null) : null),
    [task.epicId, epics]
  );
  const parentTask = useMemo(
    () => (task.parentTaskId ? (context.allTasks.find((t: Task) => t.id === task.parentTaskId) ?? null) : null),
    [task.parentTaskId, context.allTasks]
  );

  const [messageApi, contextHolder] = message.useMessage();

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [rowActionTask, setRowActionTask] = useState<Task | null>(null);

  const [confirmLoadingDeleteTask, setConfirmLoadingDeleteTask] = useState<boolean>(false);

  const titleInputRef = useRef<TitleInputRef>(null);
  const hasInputFocusedRef = useRef<boolean>(false);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const navigateToTask = useNavigateToTask();

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  const handleDelete = async (item: { id: number }) => {
    try {
      await deleteTask(item.id);
      void messageApi.success('Task deleted successfully.');

      const newData = dataSource.filter((i) => i.id !== item.id);
      setDataSource(newData);

      // Update React Query cache for cross-view consistency
      const workspaceId = typeof task.workspaceId === 'number' ? task.workspaceId : parseInt(task.workspaceId);

      queryClient.setQueryData(['tasks', workspaceId], (oldData: Task[] | undefined) => {
        if (!oldData) return oldData;
        let filtered = oldData.filter((t) => t.id !== item.id);
        if (task.type === TaskType.epic) {
          filtered = filtered.map((t) => (t.epicId === item.id ? { ...t, epicId: null } : t));
        }
        return filtered;
      });

      if (task.type === TaskType.epic) {
        queryClient.setQueryData(['epics', workspaceId], (oldData: Epic[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.filter((e) => e.id !== item.id);
        });
      }

      if (context.workspace?.id) {
        await removeFromSpaceTasksOrder(context.workspace?.id, item.id);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleRemoveFromSprint = async (item: Task) => {
    try {
      const updatedTask = { ...item, sprintId: null };
      updateTaskMutation.mutate({ taskId: item.id, task: updatedTask });

      const newData = dataSource.filter((i) => i.id !== item.id);
      setDataSource(newData);

      // The task leaves the board but not the space: patch the shared cache instead of
      // relying on the socket echo, which the other views would otherwise never see.
      const workspaceId = typeof task.workspaceId === 'number' ? task.workspaceId : parseInt(task.workspaceId);
      optimisticUpdateListItem<Task>(queryClient, queryKeys.tasks(workspaceId), updatedTask);
    } catch (e) {
      console.log(e);
    }
  };

  const handleMoveToPlaning = async (item: Task) => {
    try {
      const updTask = await moveTaskToPlaning(task.id);

      const newData = dataSource.filter((i) => i.id !== item.id);
      setDataSource(newData);

      queryClient.setQueryData(
        ['tasks', typeof task.workspaceId === 'number' ? task.workspaceId : parseInt(task.workspaceId)],
        (oldData: Task[]) => {
          const newTasksData = [...oldData].filter((oldTask) => task.id !== oldTask.id);
          return [...newTasksData, updTask.data];
        }
      );
    } catch (e) {
      console.log(e);
    }
  };

  const triggerEdit = () => {
    if (titleInputRef.current) {
      titleInputRef.current.focus();
    }
  };

  const framework = context.workspace?.framework;
  const frameworkSpecificMenuItems: MenuProps['items'] =
    framework === WorkspaceFramework.scrum
      ? [
          {
            key: 'move_to_planning',
            label: 'Move to Planning',
            icon: <LeftOutlined />,
          },
          {
            key: 'remove_from_sprint',
            label: 'Remove from Sprint',
            icon: <CloseOutlined />,
          },
        ]
      : framework === WorkspaceFramework.kanban
        ? [
            {
              key: 'remove_from_sprint',
              label: 'Remove from Kanban',
              icon: <CloseOutlined />,
            },
          ]
        : [];
  const rowMenuItems: MenuProps['items'] = [
    ...frameworkSpecificMenuItems,
    {
      key: 'rename',
      label: 'Rename',
      icon: <EditOutlined />,
    },
    {
      type: 'divider',
    },
    {
      key: 'delete',
      label: <span>Delete</span>,
      icon: <DeleteOutlined />,
      danger: true,
    },
  ];

  const onRowMenuClicked = async (e: { key: React.Key }, item: Task) => {
    setRowActionTask(item);
    if (e.key === 'delete') {
      setRowActionTask(item);
      setOpenDeleteModal(true);
    } else if (e.key === 'move_to_planning') {
      await handleMoveToPlaning(item);
    } else if (e.key === 'remove_from_sprint') {
      await handleRemoveFromSprint(item);
    } else if (e.key === 'rename') {
      triggerEdit();
    }
    console.log(e.key);
  };

  const handleConfirmDeleteTask = () => {
    setConfirmLoadingDeleteTask(true);

    if (rowActionTask) {
      handleDelete(rowActionTask)
        .then(() => {
          setRowActionTask(null);
        })
        .catch((e) => {
          console.log(e);
          void messageApi.error('Failed to delete task');
        });
    } else {
      void messageApi.error('Failed to find selected task');
    }

    setRowActionTask(null);
    setConfirmLoadingDeleteTask(false);
    setOpenDeleteModal(false);
  };

  const handleCancelTaskDelete = () => {
    setOpenDeleteModal(false);
  };

  const { dataSource, setDataSource } = context;

  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id: task.id,
    data: {
      type: 'TaskTableDataType',
      task,
    },
  });

  const style = {
    transition,
    transform: CSS.Translate.toString(transform),
  };

  const updateTaskMutation = useUpdateTaskMutation();
  const activeSprint = getCurrentWorkspaceActiveSprint();

  const handleSave = useCallback(
    (task: Task) => {
      updateTaskMutation.mutate({
        taskId: task.id,
        task: {
          ...task,
        },
      });

      // Update tasks query
      queryClient.setQueryData(
        ['tasks', typeof task.workspaceId === 'number' ? task.workspaceId : parseInt(task.workspaceId)],
        (oldData: Task[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((t) => (t.id === task.id ? { ...task } : t));
        }
      );

      if (
        viewContext?.view?.group !== WorkspaceViewGroup.sprint &&
        viewContext?.view?.group !== WorkspaceViewGroup.kanban
      ) {
        return;
      }

      // No active sprint (e.g. kanban view without a started sprint) — nothing to update.
      const activeSprintId = activeSprint?.data?.sprint?.id;
      if (!activeSprintId) return;

      // Update sprint query
      queryClient.setQueryData(
        ['sprint', parseInt(activeSprintId as string)],
        (oldSprintData: { tasks: Task[]; sprint: any } | undefined) => {
          if (!oldSprintData) return oldSprintData;
          const newTasksData = [...oldSprintData.tasks];
          const index = newTasksData.findIndex((t) => task.id === t.id);
          if (index !== -1) {
            newTasksData[index] = {
              ...task,
            };
          }
          return {
            ...oldSprintData,
            tasks: [...newTasksData],
          };
        }
      );
    },
    [activeSprint, updateTaskMutation, task, usersWithAccessToSpace, epics, taskStatuses]
  );

  // This chunk of code must be just before the return statement to avoid "rendered fewer hooks than expected" React error
  if (isDragging) {
    const isTitleInTwoLines = task.title ? task.title?.length > 28 : false;
    const heightNumber = (parentTask ? 188 : 150) + (isTitleInTwoLines ? 22 : 0);
    const height = `${heightNumber}px`;
    return (
      <div
        ref={setNodeRef}
        style={{ ...style, height, width: '286px' }}
        className={`bg-blue-100 rounded-md shadow border-dashed border border-blue-300 flex items-center justify-center text-neutral-900 text-xs font-semibold`}
      >
        {`Drop task to move it here.`}
      </div>
    );
  }

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        onFocus={(e) => {
          // Track when any input/textarea within this card gets focus
          const target = e.target as HTMLElement;
          if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
            hasInputFocusedRef.current = true;
            // Clear any pending blur timeout
            if (blurTimeoutRef.current) {
              clearTimeout(blurTimeoutRef.current);
              blurTimeoutRef.current = null;
            }
          }
        }}
        onBlur={(e) => {
          // Reset flag when input loses focus, but with a small delay
          // to handle the case where blur happens right before click
          const target = e.target as HTMLElement;
          if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
            blurTimeoutRef.current = setTimeout(() => {
              hasInputFocusedRef.current = false;
            }, 200); // Increased timeout to ensure click handler runs first
          }
        }}
        onClick={(e) => {
          if (openDeleteModal) {
            return;
          }
          // Prevent click if user has selected text (e.g., from input drag)
          const selection = window.getSelection();
          if (selection && selection.toString().length > 0) {
            return;
          }

          // More comprehensive check for interactive elements
          const target = e.target as HTMLElement;

          // Check if clicking directly on input elements
          if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
            return;
          }

          // Check if clicking on or within specific interactive elements that should prevent navigation
          const interactiveElement = target.closest(
            'input, textarea, button, [data-no-dnd="true"], .ant-dropdown-trigger, .ant-select, .ant-select-selector'
          );

          if (interactiveElement) {
            return;
          }

          // Check if any input within this card was recently focused
          if (hasInputFocusedRef.current) {
            return;
          }

          e.preventDefault();
          e.stopPropagation();
          navigateToTask(task);
        }}
        className={`left-0 top-0 bg-brand-white items-center flex text-left rounded-md shadow border border-neutral-300 ${dragging ? 'cursor-grabbing' : 'cursor-pointer'} relative select-none overflow-hidden group task-card`}
      >
        <div className="w-full h-full flex-col justify-start items-start gap-1 flex">
          {parentTask && (
            <div className="w-full p-1 flex items-center justify-start bg-neutral-500 rounded-t-md gap-1">
              <div className="flex items-center gap-1 parent-task-card">
                <div className="bg-neutral-200 flex items-center justify-center gap-1 px-2 rounded-md">
                  <SubitemIcon className="text-neutral-900" />
                  <ParentTaskDropdown
                    task={parentTask || null}
                    options={
                      dataSource.filter((t) => t.id !== task.id && t.parentTaskId === null && t.stage !== 'triage') ??
                      []
                    }
                    epics={epicsQuery.data ?? []}
                    onBlur={() => {}}
                    onSelect={async (parentTask) => {
                      handleSave({
                        ...task,
                        parentTaskId: parentTask ? parentTask.id : null,
                        epicId: parentTask ? parentTask.epicId : null,
                      });
                    }}
                    size="small"
                  />
                </div>
                <GoToIcon
                  className="text-neutral-900 bg-neutral-300 rounded p-1 cursor-pointer parent-task-card__go-to-icon"
                  onClick={(e: React.MouseEvent<SVGSVGElement>) => {
                    e.stopPropagation();
                    e.preventDefault();
                    navigateToTask(parentTask);
                  }}
                />
              </div>
            </div>
          )}
          <div className="w-full h-full px-3 relative">
            <div className=" flex flex-col justify-between items-start gap-2 h-full">
              <div className="flex items-center gap-1 w-full pt-2">
                <TaskTypeDropdown
                  type={taskType as TaskType}
                  size="small"
                  onBlur={() => {}}
                  onSelect={(type) => {
                    handleSave({ ...task, type });
                  }}
                  disabled={!!task.parentTaskId}
                />
                <div className={'text-neutral-900 text-sm font-normal'}>{`DEV-${task.taskId}`}</div>
              </div>

              <CustomTooltip
                // Show tooltip only if the title is longer than 25 characters and not dragging.
                overlayClassName={`${task.title.length > 25 && !dragging ? 'visible' : 'hidden'}`}
                placement="top"
                title={task.title}
                open={dragging ? false : undefined}
              >
                <div className="flex items-center gap-2 w-full pt-1">
                  <TitleInput
                    ref={titleInputRef}
                    task={task}
                    onClick={() => navigateToTask(task)}
                    onSave={handleSave}
                  />
                  <BaseButton
                    type="secondary"
                    size="small"
                    className="pl-1 pr-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    onClick={triggerEdit}
                  >
                    <EditOutlined />
                  </BaseButton>
                </div>
              </CustomTooltip>

              <div className="w-full flex items-center justify-start">
                <div className="flex items-center gap-1 parent-task-card">
                  <div className="h-6 flex items-center px-1 rounded-md border border-solid border-neutral-300">
                    <EpicDropdown
                      epic={epic || null}
                      epics={epics}
                      size="small"
                      onSelect={(epic) => {
                        handleSave({ ...task, epicId: epic ? epic.id : null });
                      }}
                      onBlur={() => {}}
                      disabled={!!task.parentTaskId}
                      hideOutline={true}
                    />
                  </div>
                  {epic && (
                    <GoToIcon
                      className="text-neutral-900 bg-neutral-300 rounded p-1 cursor-pointer parent-task-card__go-to-icon"
                      onClick={(e: React.MouseEvent<SVGSVGElement>) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (epic && epic.workspaceId && epic.taskId) {
                          navigateToTask(epic);
                        }
                      }}
                    />
                  )}
                </div>
              </div>

              <div className="flex h-[32px] justify-between items-stretch gap-1 w-full mb-2">
                <OwnerDropdown
                  owner={assignee}
                  owners={usersWithAccessToSpace.map((userAccess) => userAccess.User) ?? []}
                  onBlur={() => {}}
                  onSelect={(user) => {
                    handleSave({ ...task, assignee: user ? user.id : null });
                  }}
                  selectedElement={
                    <div className="flex items-center">
                      <UserAvatar assignee={assignee?.id} usersWithAccessToSpace={usersWithAccessToSpace} />
                    </div>
                  }
                  size="small"
                  hideOutline={true}
                  tooltipTitle={assignee?.fullName || assignee?.email || 'Click to assign owner'}
                />
                <div className="flex items-center gap-1">
                  <EstimateDropdown
                    estimate={estimate}
                    onSelect={(estimate) => {
                      handleSave({
                        ...task,
                        ...(context.workspace?.estimationType === WorkspaceEstimationType.storyPoints
                          ? { storyPoints: estimate }
                          : { estimate }),
                      });
                    }}
                    size="small"
                    hideOutline={true}
                    emptyElement={
                      <div className={`flex px-2 py-0.5 bg-neutral-200 rounded justify-center items-center`}>
                        <EstimateIcon className="text-neutral-900" />
                      </div>
                    }
                  />
                  <PriorityDropdown
                    priority={priority ?? PriorityOptions.NoPriority}
                    onSelect={(priority) => {
                      handleSave({
                        ...task,
                        priority,
                      });
                    }}
                    size="small"
                    hideOutline={true}
                  />
                </div>
              </div>
            </div>

            <div className="right-3 top-0 pt-2 absolute opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <div className="w-6 h-6 bg-brand-white rounded outline hover:bg-neutral-200 outline-neutral-300 flex content-center justify-center cursor-pointer">
                <BaseDropdown
                  menu={{
                    items: rowMenuItems,
                    onClick: (e) => {
                      onRowMenuClicked(e, task);
                      e.domEvent.stopPropagation();
                    },
                  }}
                  trigger={['click']}
                >
                  <MoreOutlined
                    className="text-slate-400"
                    style={{ fontSize: '18px', fontWeight: 800 }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </BaseDropdown>
              </div>
            </div>
          </div>
        </div>

        <DeleteTaskModal
          task={task}
          subtasks={task.subtasks || []}
          open={openDeleteModal}
          onDelete={handleConfirmDeleteTask}
          onCancel={handleCancelTaskDelete}
        />
      </div>
    </>
  );
}

// Memoize TaskCard to prevent unnecessary re-renders
// With virtualization, this provides additional benefit when scrolling
export default memo(TaskCard);
